/**
 * Time: capture on one tab, the log on the other.
 *
 * The split is the standalone tracker's, and it is the right one — "what am I
 * doing now" and "what did I do" are different questions asked at different
 * moments, and answering both on one screen meant the capture bar sat above a
 * wall of history nobody reads while logging.
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useChrome } from "@/context/ChromeContext";
import { HeaderTabs } from "@/components/HeaderTabs";
import { useToast } from "@/context/ToastContext";
import { itemProjectId } from "@/components/ItemRow";
import { useAllItems, useAllPhases, useProjects } from "@/hooks/useData";
import { effectiveRate, useClients, useTimeMutations } from "@/hooks/useTime";
import { KindPicker } from "@/components/KindPicker";
import { defaultKind, fitKind, kindChoices, kindFields, kindIn, type EntryKind } from "@/lib/billing";
import { groupTasksByPhase } from "@/lib/taskOptions";
import { TaskCombo } from "@/components/TaskCombo";
import { isTravelActivity, loadTravel, travelRate } from "@/lib/travel";
import { entryDayKey, parseDuration } from "@/lib/timeRollup";
import { useIsMobile } from "@/lib/useMediaQuery";
import type { Client, Item, Phase, Project, TimeEntry } from "@/lib/types";

import { EntriesTab, type EntrySpan } from "./time/EntriesTab";

const SPAN_PARAMS: EntrySpan[] = ["week", "month", "quarter"];
import { TodayTab } from "./time/TodayTab";

const TABS: [Tab, string][] = [["today", "Today"], ["entries", "Entries"]];
type Tab = "today" | "entries";

/** An empty entry for "+ Add": today, half an hour, nothing chosen. */
function blankEntry(): TimeEntry {
  const end = new Date();
  const start = new Date(end.getTime() - 30 * 60_000);
  return {
    id: "", start: start.toISOString(), end: end.toISOString(),
    durationSeconds: 30 * 60, note: "", billable: true,
  } as TimeEntry;
}

export function Time() {
  const mobile = useIsMobile();

  /**
   * The tab, and any filter arriving with it, come from the URL.
   *
   * Reports links here when you click a project or client — "/time?tab=
   * entries&project=…&period=month&offset=-1" — so the entries behind a figure open where they can be corrected
   * rather than being duplicated into a second list over there. It also makes
   * a filtered view something you can bookmark.
   */
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(params.get("tab") === "entries" ? "entries" : "today");
  const [editing, setEditing] = useState<TimeEntry | null>(null);

  // The query is an instruction, not state: consume it so a later tab change
  // does not silently re-apply a filter you have since cleared.
  const arriving = useMemo(
    () => ({
      projectId: params.get("project") ?? undefined,
      activity: params.get("activity") ?? undefined,
      clientId: params.get("client") ?? undefined,
      allTime: params.get("all") === "1",
      span: SPAN_PARAMS.find((s) => s === params.get("period")),
      offset: Number(params.get("offset")) || 0,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useEffect(() => {
    if (params.toString()) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabsNode = <HeaderTabs value={tab} options={TABS} onChange={setTab} />;

  useChrome({ title: "Time", subtitle: "Track and log your hours", tabs: tabsNode }, [tab]);

  return (
    <div style={{ padding: mobile ? "16px 16px 90px" : "20px 36px 90px", maxWidth: 1180 }}>
      {tab === "today"
        ? <TodayTab onShowEntries={() => setTab("entries")} onEditEntry={setEditing} />
        : (
          <EntriesTab
            onEditEntry={setEditing}
            onAddEntry={() => setEditing(blankEntry())}
            initialProjectId={arriving.projectId}
            initialClientId={arriving.clientId}
            initialActivity={arriving.activity}
            initialAllTime={arriving.allTime}
            initialSpan={arriving.span}
            initialOffset={arriving.offset}
          />
        )}

      {editing && <EntryEditor entry={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

/**
 * The entry dialog with its saving wired in, so any screen that shows an entry
 * (Time's tabs, the Today grid) opens the same one. An entry with no id is a
 * blank draft and is created rather than patched.
 */
export function EntryEditor({ entry, onClose }: { entry: TimeEntry; onClose: () => void }) {
  const { data: projects = [] } = useProjects();
  const { data: items = [] } = useAllItems();
  const { data: phases = [] } = useAllPhases();
  const { data: clients = [] } = useClients();
  const { logEntry, updateEntry, deleteEntry } = useTimeMutations();
  const toast = useToast();
  const selectable = useMemo(() => projects.filter((p) => p.kind !== "inbox"), [projects]);

  return (
    <EditEntryModal
      entry={entry}
      projects={selectable}
      allProjects={projects}
      clients={clients}
      items={items}
      phases={phases}
      onCancel={onClose}
      onDelete={entry.id ? async () => {
        try {
          await deleteEntry.mutateAsync(entry.id);
          toast("Entry deleted");
          onClose();
        } catch {
          toast("Could not delete that entry");
        }
      } : undefined}
      onSave={async (patch) => {
        try {
          if (entry.id) {
            await updateEntry.mutateAsync({ id: entry.id, ...patch });
            toast("Entry updated");
          } else {
            // A blank draft is a new entry: the same seven fields, so the
            // same dialog, but it creates rather than patches — and picks
            // up the rate at creation the way every other write does.
            const r = effectiveRate(projects.find((p) => p.id === patch.project), clients);
            const k = kindFields(kindIn({ ...patch, client: r.clientId ?? undefined }, projects), r.rate);
            await logEntry.mutateAsync({
              projectId: patch.project || undefined,
              itemId: patch.item || undefined,
              start: patch.start!, end: patch.end!,
              durationSeconds: patch.durationSeconds!,
              note: patch.note ?? "",
              category: patch.category || undefined,
              billable: k.billable, internal: k.internal,
              source: "manual",
              rate: k.rate, currency: r.currency, clientId: r.clientId,
            });
            toast("Entry added");
          }
          onClose();
        } catch {
          toast(entry.id ? "Could not update that entry" : "Could not add that entry");
        }
      }}
    />
  );
}

/**
 * Correcting an entry after the fact — a timer left running through lunch, a
 * call logged against the wrong project. Duration is edited as a length, not as
 * an end time, because that is how you remember it; the end is recomputed from
 * the start so the entry stays internally consistent.
 */
function EditEntryModal({ entry, projects, allProjects, clients, items, phases, onSave, onDelete, onCancel }: {
  entry: TimeEntry;
  projects: { id: string; name: string }[];
  /** Every project, with billing types, so the kind can be worked out. */
  allProjects: Project[];
  clients: Client[];
  items: Item[];
  phases: Phase[];
  onSave: (patch: Partial<TimeEntry>) => void | Promise<void>;
  /** Absent for a new entry — there is nothing yet to remove. */
  onDelete?: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const [note, setNote] = useState(entry.note ?? "");
  const [itemId, setItemId] = useState(entry.item ?? "");
  const [length, setLength] = useState(() => {
    const total = Math.round((entry.durationSeconds || 0) / 60);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
  });
  const [project, setProject] = useState(entry.project ?? "");
  /** Billable / No charge / Project work / Internal. An existing entry opens on
   *  its own kind; a new one follows the project. */
  const [kindPick, setKindPick] = useState<EntryKind | null>(() => (entry.id ? kindIn(entry, allProjects) : null));
  const projectRec = allProjects.find((p) => p.id === project);
  const derived = effectiveRate(projectRec, clients);
  const hasClient = !!(derived.clientId || entry.client);
  const kind = fitKind(kindPick ?? defaultKind(projectRec, hasClient), projectRec, hasClient);
  const charged = kind === "hourly";
  // entryDayKey, not start.slice(0,10): the stored start is UTC, so anything
  // logged after ~5pm Pacific slices to TOMORROW's date and the dialog opens
  // claiming the wrong day. The day an entry belongs to is its LOCAL day —
  // which is also what the entries list groups by, so the two now agree.
  const [day, setDay] = useState(entryDayKey(entry));
  const [error, setError] = useState<string | null>(null);
  const [clearInvoiced, setClearInvoiced] = useState(false);
  const travelSetting = loadTravel();
  const [travel, setTravel] = useState(Boolean(entry.travelPct));
  // An entry already at a travel share keeps its own; a new one takes today's.
  const travelPct = entry.travelPct || travelSetting.pct;

  // Same phase-grouped picker as the quick log, so the two cannot present the
  // same project's tasks differently.
  const taskGroups = groupTasksByPhase(items, phases, project || undefined, itemProjectId);

  function submit() {
    const minutes = parseDuration(length);
    if (minutes === null || minutes <= 0) { setError("Enter a length like 1:30, 90 or 1.5h"); return; }

    // Keep the original time of day, move the calendar day if it changed, and
    // recompute the end from the new length.
    const start = new Date(entry.start);
    const [y, m, d] = day.split("-").map(Number);
    start.setFullYear(y, m - 1, d);
    const end = new Date(start.getTime() + minutes * 60_000);

    const k = kindFields(kind, null);
    void onSave({
      note,
      billable: k.billable,
      internal: k.internal,
      project: project || undefined,
      item: itemId || undefined,
      durationSeconds: minutes * 60,
      start: start.toISOString(),
      end: end.toISOString(),
      // Only Billable time keeps a rate, or the amount column lies.
      ...travelPatch(),
      // Empty string, not undefined: PocketBase clears a date on "", and
      // leaves it alone when the key is missing.
      ...(clearInvoiced ? { invoicedAt: "", invoiceNumber: "" } : null),
    });
  }

  /**
   * The rate and category that follow from the kind and the travel box.
   * The full rate is recovered from the entry's own `travelPct`, so switching
   * travel off restores exactly what it was — whatever the setting says now.
   */
  function travelPatch(): Partial<TimeEntry> {
    // An entry with no rate on record (it was project work, say) takes the
    // current rate when it becomes Billable.
    const full = (entry.rateSnapshot != null && entry.travelPct
      ? Math.round((entry.rateSnapshot / (entry.travelPct / 100)) * 100) / 100
      : entry.rateSnapshot) || derived.rate;
    const wasTravelActivity = isTravelActivity(entry.category, travelSetting);
    const category = travel && !entry.category ? travelSetting.activity
      : !travel && wasTravelActivity ? ""
      : entry.category;
    if (!entry.id) return { category: travel ? travelSetting.activity : entry.category };
    return {
      category,
      rateSnapshot: !charged || full == null ? (null as unknown as undefined) : travel ? travelRate(full, travelPct) : full,
      travelPct: charged && travel && full != null ? travelPct : (null as unknown as undefined),
    };
  }

  return (
    <div onClick={onCancel} style={{
      position: "fixed", inset: 0, background: "var(--scrim)", zIndex: 60,
      display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "60px 20px", overflowY: "auto",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 440, maxWidth: "100%", background: "var(--raised)", borderRadius: 14,
        padding: "22px 24px", border: "1px solid var(--overlayLine)", boxShadow: "var(--shadowOverlay)",
      }}>
        <h2 style={{ margin: "0 0 16px", fontSize: 17, fontWeight: 500 }}>{entry.id ? "Edit entry" : "New entry"}</h2>

        {/* Not a lock: invoices get corrected. But the invoice already sent
            will not change with this entry, and that is worth saying first. */}
        {entry.invoicedAt && (
          <div style={{
            fontSize: 12, lineHeight: 1.45, color: "var(--ink2)", marginBottom: 14,
            padding: "9px 11px", borderRadius: 8, background: "var(--warnTint)", border: "1px solid var(--line2)",
          }}>
            Invoiced {new Date(entry.invoicedAt).toLocaleDateString()}
            {entry.invoiceNumber ? ` on ${entry.invoiceNumber}` : ""}. Changes here won't change the invoice you sent.
            <label style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 7, color: "var(--ink3)" }}>
              <input type="checkbox" checked={clearInvoiced} onChange={(e) => setClearInvoiced(e.target.checked)} />
              Remove the invoiced mark (it goes back to unbilled)
            </label>
          </div>
        )}

        <label style={modalLabel}>
          Description
          <input value={note} onChange={(e) => setNote(e.target.value)} style={input} autoFocus />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={modalLabel}>
            Length (h:mm)
            <input value={length} onChange={(e) => { setLength(e.target.value); setError(null); }} style={input} placeholder="1:30" />
            <span style={{ display: "flex", gap: 5, marginTop: 6 }}>
              {[15, 30, 60].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setLength(`${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`); setError(null); }}
                  style={{ padding: "3px 9px", borderRadius: 999, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink3)", fontSize: 11.5, cursor: "pointer" }}
                >{m < 60 ? `${m}m` : `${m / 60}h`}</button>
              ))}
            </span>
          </label>
          <label style={modalLabel}>
            Date
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} style={input} />
          </label>
        </div>

        <label style={modalLabel}>
          Project
          {/* Changing the project clears the task, as the quick log does on
              scope change — otherwise an entry strands on a task belonging to a
              different project. */}
          <select
            value={project}
            onChange={(e) => { setProject(e.target.value); setItemId(""); }}
            style={input}
          >
            <option value="">— No project (ad-hoc)</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>

        <label style={modalLabel}>
          Task <span style={{ color: "var(--muted2)" }}>(optional — time can sit on the project)</span>
          <TaskCombo value={itemId} onChange={setItemId} groups={taskGroups} disabled={!project} style={input} />
        </label>

        <div style={{ margin: "4px 0 14px" }}>
          <KindPicker value={kind} choices={kindChoices(projectRec, hasClient)} onChange={setKindPick} fromProject={!kindPick} />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: charged ? "var(--ink2)" : "var(--muted2)", margin: "-4px 0 14px" }}>
          <input type="checkbox" checked={travel} disabled={!charged} onChange={(e) => setTravel(e.target.checked)} />
          Travel — billed at {travelPct}% of the rate
        </label>

        {error && <div style={{ fontSize: 12, color: "var(--bad)", marginBottom: 10 }}>{error}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {/* Opposite corner from Save, because the two are one mis-aim apart
              and only one of them is reversible. */}
          {onDelete && (
            <button
              onClick={() => void onDelete()}
              style={{ ...ghostBtn, marginRight: "auto", color: "var(--bad)", borderColor: "var(--line2)" }}
            >Delete</button>
          )}
          <button onClick={onCancel} style={ghostBtn}>Cancel</button>
          <button onClick={submit} style={primaryBtn}>Save</button>
        </div>
      </div>
    </div>
  );
}

const modalLabel: React.CSSProperties = { display: "block", fontSize: 11.5, color: "var(--muted)", marginBottom: 12 };

const input: React.CSSProperties = {
  width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 13,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
};
const fieldLabel: React.CSSProperties = { display: "block", fontSize: 11.5, color: "var(--muted)" };
const primaryBtn: React.CSSProperties = {
  padding: "8px 16px", borderRadius: 999, border: "none",
  background: "var(--accent)", color: "var(--onAccent)", fontSize: 12.5, cursor: "pointer",
};
const navBtn: React.CSSProperties = {
  width: 26, height: 26, borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 14, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
};
const ghostBtn: React.CSSProperties = {
  padding: "8px 14px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer",
};
